import { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { getLastObject, noopFunction } from '@vestico/utils';

import { SupportedMatchInfoHashCategory, userDataCompleted } from '@vestico/model';

import {
  Button,
  ButtonDefinition,
  ButtonStyle,
  ButtonsRow,
  Footer,
  GlobalsContext,
  Horizontal,
  NavigationContainer,
  NavigationContainerProps,
  RoutePathMatchedResults,
  StepConfig,
  UserContext,
  Vertical,
  getStepConfigs,
  i18nBackButtonTitle,
  i18nCancelButtonTitle,
  i18nNextButtonTitle,
} from '@vestico/shared';

import { LogMatchInfoAnalytics } from './LogMatchInfoAnalytics';

export interface WidgetNavigationControllerProps {
  category?: SupportedMatchInfoHashCategory;
  postSteps?: StepConfig[];
  showPrivacyOnMobile?: boolean;
  showBackButtonAtLastStep?: boolean;
  showCancelButtonAtFirstStep?: boolean;
  didPressFirstStepCancelButton?: () => void;
  onSetCurrentStep?: (step?: StepConfig) => void;
  onFinishUserMatchInfoForm?: () => void;
}

export const WidgetNavigationController = ({
  category,
  postSteps,
  showPrivacyOnMobile,
  showCancelButtonAtFirstStep,
  didPressFirstStepCancelButton,
  onSetCurrentStep,
  onFinishUserMatchInfoForm,
}: WidgetNavigationControllerProps) => {
  const { user, reloadUser } = useContext(UserContext);
  const navigate = useNavigate();
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [pushed, setPushed] = useState(false);
  const [stepConfigs, setStepConfigs] = useState<StepConfig[]>([]);

  useEffect(() => {
    setPushed(false);
    setStepConfigs(getStepConfigs(user, category, undefined, postSteps));
    setCurrentStepIndex(0);
  }, [user, postSteps, category]);

  useEffect(() => {
    if (onSetCurrentStep) {
      onSetCurrentStep(stepConfigs[currentStepIndex]);
    }
  }, [onSetCurrentStep, currentStepIndex, stepConfigs]);

  const onFinishUserMatchInfoFormStep = useCallback(() => {
    if (reloadUser) {
      reloadUser(false);
    }
    if (onFinishUserMatchInfoForm) {
      onFinishUserMatchInfoForm();
    }
  }, [onFinishUserMatchInfoForm, reloadUser]);

  const [validateStep, setValidateStep] = useState<boolean>(false);
  const [clickedButton, setClickedButton] = useState<ButtonDefinition | null>(null);
  const [buttonsDisabled, setButtonsDisabled] = useState(false);

  const goBack = useCallback(() => {
    if (currentStepIndex === 0) {
      return;
    }
    setCurrentStepIndex(currentStepIndex - 1);
  }, [currentStepIndex]);

  const isMatchInfoFormStep = useMemo(() => {
    if (currentStepIndex > stepConfigs.length - 1 || !user) {
      return false;
    }
    const currentConfig = stepConfigs[currentStepIndex];
    let lastConfig = getLastObject(stepConfigs);
    // We take the second last one if the last one is match results
    if (lastConfig?.path === RoutePathMatchedResults) {
      lastConfig = stepConfigs[stepConfigs.length - 2];
    }

    return currentConfig.path === lastConfig?.path;
  }, [currentStepIndex, stepConfigs, user]);

  const leftButton: ButtonDefinition | undefined = useMemo(() => {
    // Don't show a button when there are no body info form steps
    if (stepConfigs.length - (postSteps?.length ?? 0) === 0) {
      return undefined;
    }

    let title: string;
    let onClickAction: () => void;

    if (currentStepIndex === 0) {
      if (!showCancelButtonAtFirstStep) {
        return undefined;
      }
      title = i18nCancelButtonTitle();
      onClickAction = didPressFirstStepCancelButton ?? noopFunction;
    } else {
      title = i18nBackButtonTitle();
      onClickAction = goBack;
    }

    const button: ButtonDefinition = {
      title: title,
      disabled: buttonsDisabled,
      style: ButtonStyle.back,
      onClick: onClickAction,
      action: noopFunction,
    };
    return button;
  }, [stepConfigs.length, postSteps?.length, currentStepIndex, buttonsDisabled, showCancelButtonAtFirstStep, didPressFirstStepCancelButton, goBack]);

  const onSubmitButton = useCallback(
    (button: ButtonDefinition) => () => {
      setValidateStep(true);
      setClickedButton(button);
    },
    []
  );

  const onMoveToNextStep = useCallback(() => {
    if (isMatchInfoFormStep) {
      onFinishUserMatchInfoFormStep();
      return;
    }
    setCurrentStepIndex(currentStepIndex + 1);
  }, [currentStepIndex, isMatchInfoFormStep, onFinishUserMatchInfoFormStep]);

  const rightButton: ButtonDefinition | undefined = useMemo(() => {
    // Don't show a button when there are no body info form steps
    if (stepConfigs.length - (postSteps?.length ?? 0) === 0) {
      return undefined;
    }

    const nextButton: ButtonDefinition = {
      title: i18nNextButtonTitle(),
      disabled: buttonsDisabled,
      style: ButtonStyle.default,
      onClick: noopFunction,
      action: onMoveToNextStep,
    };

    nextButton.onClick = onSubmitButton(nextButton);

    return nextButton;
  }, [stepConfigs.length, postSteps?.length, buttonsDisabled, onMoveToNextStep, onSubmitButton]);

  const onSubmit = useCallback(() => {
    if (rightButton) {
      rightButton.onClick();
    }
  }, [rightButton]);

  const navigationButtons = useMemo(() => {
    const navButtons = [];
    if (leftButton || rightButton) {
      if (leftButton) {
        navButtons.push(leftButton);
      } else {
        navButtons.push(<div />);
      }
      if (rightButton) {
        navButtons.push(rightButton);
      }
    }

    if (navButtons.length === 0) {
      return null;
    }

    return <ButtonsRow leftButton={leftButton} rightButton={rightButton} />;
  }, [leftButton, rightButton]);

  const onAnimate = useCallback((animating: boolean) => setButtonsDisabled(animating), []);

  const { useMobileLayout, stylesConfig } = useContext(GlobalsContext);
  const { customPrivacyURL } = stylesConfig.stylesConfig;

  const { t } = useTranslation();
  const reset = useCallback(() => {
    if (user) {
      if (reloadUser) {
        reloadUser(true);
      }
    }
  }, [reloadUser, user]);

  const showFooter = useMemo(() => useMobileLayout && !!showPrivacyOnMobile, [showPrivacyOnMobile, useMobileLayout]);

  const rightFooterActions = useMemo(
    () =>
      showFooter && user && userDataCompleted(user, category, false, true)
        ? [
            <div key="resetButton">
              <Button content={t('LOC_RESET_BODY_INFO_BUTTON')} onClick={reset} plain size="small" />
            </div>,
          ]
        : undefined,
    [category, reset, showFooter, t, user]
  );

  const footer = useMemo(() => {
    if (stepConfigs.length === 0) {
      return undefined;
    }

    return (
      <Footer
        showPrivacyLink={showFooter}
        showBanner={showFooter}
        fullWidth={currentStepIndex !== undefined && stepConfigs[currentStepIndex]?.fullWidth}
        privacyPosition={
          useMobileLayout ? { horizontal: Horizontal.left, vertical: Vertical.top } : { horizontal: Horizontal.center, vertical: Vertical.top }
        }
        customPrivacyURL={customPrivacyURL}
        rightActions={rightFooterActions}
        padding={currentStepIndex !== undefined ? stepConfigs[currentStepIndex]?.footerPadding : undefined}
      />
    );
  }, [stepConfigs, showFooter, currentStepIndex, useMobileLayout, customPrivacyURL, rightFooterActions]);

  const handleCurrentStepIsValid = useCallback(
    (valid: boolean) => {
      if (valid && clickedButton) {
        if (clickedButton.action) {
          clickedButton.action();
        }

        setClickedButton(null);
      }
      setValidateStep(false);
    },
    [clickedButton]
  );

  const navigationProps: NavigationContainerProps = useMemo(() => {
    return {
      steps: stepConfigs,
      currentStepIndex: currentStepIndex,
      validateStep: validateStep,
      currentStepIsValid: handleCurrentStepIsValid,
      onSubmit,
      onAnimate,
      navigationButtons,
      footer,
    };
  }, [stepConfigs, currentStepIndex, validateStep, handleCurrentStepIsValid, onSubmit, onAnimate, navigationButtons, footer]);

  useLayoutEffect(() => {
    if (!pushed && stepConfigs.length > 0) {
      navigate(stepConfigs[0].path, { replace: true });
      setPushed(true);
    }
  }, [pushed, navigate, stepConfigs]);

  if (!pushed || stepConfigs.length === 0) {
    return null;
  }

  return (
    <LogMatchInfoAnalytics currentStepIndex={currentStepIndex} stepConfigs={stepConfigs}>
      <NavigationContainer {...navigationProps} />
    </LogMatchInfoAnalytics>
  );
};
